import { useEffect, useState } from "react";
import { api, fmtNum } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Users as UsersIcon, Plus, Pencil, Trash2, Shield, KeyRound, Lock } from "lucide-react";

const ROLE_META = {
  admin: {
    label: "Admin",
    desc: "Full access including user management",
    bg: "bg-purple-100",
    text: "text-purple-700",
    ring: "ring-purple-200",
    icon: Shield,
  },
  controller: {
    label: "Controller",
    desc: "Create / edit / delete policies, JEs & uploads",
    bg: "bg-blue-100",
    text: "text-blue-700",
    ring: "ring-blue-200",
    icon: KeyRound,
  },
  user: {
    label: "Viewer",
    desc: "Read-only access across the workspace",
    bg: "bg-slate-100",
    text: "text-slate-600",
    ring: "ring-slate-200",
    icon: Lock,
  },
};

function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return String(v);
  }
}

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.user;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${m.bg} ${m.text} ring-1 ${m.ring}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2.25} />
      {m.label}
    </span>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const canAdmin = me?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // user or {} for new
  const [confirmDelete, setConfirmDelete] = useState(null);

  function refresh() {
    setLoading(true);
    api
      .get("/users")
      .then((r) => setItems(r.data.items))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refresh();
  }, []);

  const counts = {
    admin: items.filter((u) => u.role === "admin").length,
    controller: items.filter((u) => u.role === "controller").length,
    user: items.filter((u) => u.role === "user").length,
  };

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="users-page">
      {/* Header */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center ring-1 ring-purple-100">
            <UsersIcon className="w-5 h-5 text-purple-600" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold tracking-tight">Team & access</h2>
            <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">
              Manage who can sign in to the MGA workspace and what they can do. Every change here is
              recorded in the activity log.
            </p>
          </div>
          {canAdmin && (
            <Button
              onClick={() => setEditing({})}
              data-testid="users-new-button"
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-1" /> Invite user
            </Button>
          )}
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-slate-100">
          {["admin", "controller", "user"].map((r) => {
            const m = ROLE_META[r];
            const Icon = m.icon;
            return (
              <div key={r} className={`rounded-lg p-4 ring-1 ${m.ring} ${m.bg}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${m.text}`} strokeWidth={2} />
                  <div className={`text-[11px] uppercase tracking-wider font-semibold ${m.text}`}>
                    {m.label}
                  </div>
                </div>
                <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 tabular-nums">
                  {fmtNum(counts[r])}
                </div>
                <div className="text-[11px] text-slate-600 mt-0.5">{m.desc}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
          <div className="text-[12px] text-slate-600">
            <span className="font-medium text-slate-900">{fmtNum(items.length)}</span> users
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="px-5 py-3 font-medium">User</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Created</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-slate-400 text-sm">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-slate-400 text-sm">
                    No users.
                  </td>
                </tr>
              )}
              {items.map((u) => {
                const isSelf = u.id === me?.id;
                return (
                  <tr
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-[12px] font-medium text-slate-700">
                          {(u.name || u.email).slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-slate-900 font-medium flex items-center gap-2">
                            {u.name || "—"}
                            {isSelf && (
                              <span className="text-[10px] uppercase tracking-wider text-blue-600 bg-blue-50 ring-1 ring-blue-100 px-1.5 py-0.5 rounded">
                                You
                              </span>
                            )}
                          </div>
                          <div className="text-[12px] text-slate-500 truncate">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <RoleBadge role={u.role} />
                    </td>
                    <td className="px-5 py-3 text-slate-600">{fmtDate(u.created_at)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {canAdmin ? (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            data-testid={`user-edit-${u.id}`}
                            onClick={() => setEditing(u)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                            disabled={isSelf}
                            title={isSelf ? "You can't delete yourself" : "Delete user"}
                            data-testid={`user-delete-${u.id}`}
                            onClick={() => setConfirmDelete(u)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      ) : (
                        <span className="text-[11px] text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <UserEditDialog
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refresh();
        }}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this user?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{confirmDelete?.name || confirmDelete?.email}</b> will lose access immediately. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="user-delete-confirm"
              onClick={async () => {
                try {
                  await api.delete(`/users/${confirmDelete.id}`);
                  toast.success("User removed");
                  refresh();
                } catch (e) {
                  toast.error(e.response?.data?.detail || e.message);
                } finally {
                  setConfirmDelete(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UserEditDialog({ user, onClose, onSaved }) {
  const isOpen = user !== null;
  const isCreate = isOpen && !user?.id;
  const [draft, setDraft] = useState({ email: "", name: "", password: "", role: "user" });
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    if (isCreate) {
      setDraft({ email: "", name: "", password: "", role: "user" });
    } else {
      setDraft({ email: user.email, name: user.name || "", password: "", role: user.role });
    }
    setInviteResult(null);
  }, [user, isOpen, isCreate]);

  async function save() {
    if (isCreate && !draft.email?.trim()) {
      toast.error("Email is required");
      return;
    }
    if (draft.password && draft.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      if (isCreate) {
        // password is optional — backend generates + emails a temp password if omitted
        const body = { email: draft.email, name: draft.name, role: draft.role };
        if (draft.password) body.password = draft.password;
        const { data } = await api.post("/users", body);
        setInviteResult(data);
        const emailStatus = data.email_delivery?.status;
        if (emailStatus === "sent") {
          toast.success("Invite email sent");
        } else if (emailStatus === "logged") {
          toast.message("User created. Email service is not configured — temp password shown below.");
        } else {
          toast.warning("User created, but the invite email could not be delivered. Share the temp password manually.");
        }
      } else {
        const patch = { name: draft.name, role: draft.role };
        if (draft.password) patch.password = draft.password;
        const { data } = await api.put(`/users/${user.id}`, patch);
        const results = data.email_delivery || {};
        const failed = Object.entries(results).filter(([, v]) => v?.status === "failed");
        if (failed.length) {
          toast.warning(`Saved. ${failed.length} notification email${failed.length > 1 ? "s" : ""} failed to send.`);
        } else {
          toast.success("User updated");
        }
        onSaved();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  function finishInvite() {
    setInviteResult(null);
    onSaved();
  }

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && (inviteResult ? finishInvite() : onClose())}>
      <DialogContent className="sm:max-w-md" data-testid="user-edit-dialog">
        {inviteResult ? (
          <InviteSuccess invite={inviteResult} onDone={finishInvite} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-base">
                {isCreate ? "Invite user" : `Edit ${user?.email}`}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {isCreate
                  ? "We'll email them a temporary password and ask them to set their own on first sign-in."
                  : "Change the name, role, or reset this user's password. They'll be notified by email."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Email</Label>
                <Input
                  data-testid="user-field-email"
                  type="email"
                  value={draft.email}
                  disabled={!isCreate}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                  placeholder="name@playershealth.com"
                />
                {!isCreate && (
                  <div className="text-[11px] text-slate-400">Email cannot be changed.</div>
                )}
              </div>

              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Name</Label>
                <Input
                  data-testid="user-field-name"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Jane Doe"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Role</Label>
                <Select value={draft.role} onValueChange={(v) => setDraft({ ...draft, role: v })}>
                  <SelectTrigger data-testid="user-field-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_META).map(([key, m]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <m.icon className="w-3.5 h-3.5" />
                          <span>{m.label}</span>
                          <span className="text-[11px] text-slate-400">— {m.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!isCreate && (
                <div className="space-y-1">
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                    Reset password (optional)
                  </Label>
                  <Input
                    data-testid="user-field-password"
                    type="password"
                    value={draft.password}
                    onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                    placeholder="Leave empty to keep current"
                  />
                  {draft.password && (
                    <div className="text-[11px] text-blue-600">
                      We'll email them the new password and require them to change it on sign-in.
                    </div>
                  )}
                </div>
              )}

              {isCreate && (
                <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5 text-[12px] text-slate-600 leading-relaxed">
                  A secure temporary password will be generated and emailed to the invitee.
                  They'll be prompted to choose their own password on first sign-in.
                </div>
              )}
            </div>

            <DialogFooter className="mt-4 pt-4 border-t border-slate-100">
              <Button variant="outline" onClick={onClose} data-testid="user-cancel">
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                data-testid="user-save"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? "Saving…" : isCreate ? "Send invite" : "Save changes"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InviteSuccess({ invite, onDone }) {
  const delivery = invite.email_delivery || {};
  const status = delivery.status;
  const temp = invite.temp_password;
  const sent = status === "sent";
  const [copied, setCopied] = useState(false);

  async function copyTemp() {
    try {
      await navigator.clipboard.writeText(temp || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  }

  return (
    <div data-testid="invite-success" className="space-y-4">
      <DialogHeader>
        <DialogTitle className="text-base">
          {sent ? "Invite sent" : "User created"}
        </DialogTitle>
        <DialogDescription className="text-xs">
          {sent
            ? `An invite email has been sent to ${invite.email}. They'll be asked to choose a new password on first sign-in.`
            : `The account for ${invite.email} is ready, but the invite email could not be delivered (${delivery.detail || "unknown error"}). Share the temporary password with them manually.`}
        </DialogDescription>
      </DialogHeader>

      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-medium">
          Account
        </div>
        <div className="px-4 py-3 space-y-2 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Email</span>
            <span className="font-medium text-slate-900 truncate">{invite.email}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-slate-500">Role</span>
            <span className="font-medium text-slate-900 capitalize">{invite.role}</span>
          </div>
          {temp && (
            <div className="pt-2 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 font-medium">
                Temporary password
              </div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="invite-temp-password"
                  className="flex-1 font-mono text-[13px] bg-blue-50 text-blue-900 px-2.5 py-1.5 rounded ring-1 ring-blue-100 tracking-wide"
                >
                  {temp}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={copyTemp}
                  data-testid="invite-copy-password"
                >
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5">
                Only shown once. Save it if you need to share it manually.
              </div>
            </div>
          )}
        </div>
      </div>

      <DialogFooter className="pt-2">
        <Button
          onClick={onDone}
          data-testid="invite-success-close"
          className="bg-blue-600 hover:bg-blue-700 w-full"
        >
          Done
        </Button>
      </DialogFooter>
    </div>
  );
}
